import * as yup from 'yup';

import { BidSheetRow } from '../../../hooks/useBidSheetState';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

const getElementSchema = (bid: BidSheetRow, name: 'volume' | 'bidPrice' | 'downPayment' | 'discount', t: TFunction) => {
  const { steps } = bid[`${name}Config`];

  if (steps?.length === 2) {
    return yup
      .number()
      .required(t('errors.required'))
      .min(steps[0], t('errors.minNumber', { count: steps[0] }))
      .max(steps[1], t('errors.maxNumber', { count: steps[1] }));
  }

  if (steps?.length === 4) {
    return yup
      .number()
      .required(t('errors.required'))
      .min(steps[0], t('errors.minNumber', { count: steps[0] }))
      .max(steps[3], t('errors.maxNumber', { count: steps[3] }));
  }

  return yup
    .number()
    .required(t('errors.required'))
    .min(0, t('errors.minNumber', { count: 0 }));
};

const useSchema = (bid: BidSheetRow) => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      yup.object().shape({
        volume: getElementSchema(bid, 'volume', t),
        bidPrice: getElementSchema(bid, 'bidPrice', t),
        downPayment: getElementSchema(bid, 'downPayment', t),
        discount: getElementSchema(bid, 'discount', t),
        discountedCredits: yup.number().test((value, context) => {
          if (value !== undefined && context.parent.volume !== undefined && value > context.parent.volume) {
            return context.createError({ message: t('pages.bidSheet.errors.tooManyDiscountedCredits') });
          }

          return true;
        }),
        total: yup.number().positive(t('pages.bidSheet.errors.negativeTotalBid')),
        averagePrice: yup.number().positive(t('pages.bidSheet.errors.negativeAvgPrice')),
      }),
    [bid, t],
  );
};

export default useSchema;
